import { PerformanceReportIcon } from 'ui/components/svg'
import ReportCardV2 from '../../components/report-card-v2/ReportCardV2'
import { FC, useMemo, useRef, useState } from 'react'
import { hasAnyRole, Role } from '../../../../../lib/utils'
import { useAuth } from '@frontegg/react'
import { TenantServiceV3 } from 'services'
import { ReportType, CreateReportRequest } from 'models'
import { useAppDispatch } from 'store/hooks'
import { openToast, closeToast, ToastVariant } from 'store/toasts/toastSlice'
import { generateAndDownloadReport } from '../../reports-v2.utils'

interface PerformanceReportProps {
  noActiveCpis?: boolean
  onTemplateSaved?: (generatedTemplateId?: string) => void
}

const PerformanceReport: FC<PerformanceReportProps> = ({ noActiveCpis, onTemplateSaved }) => {
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const hasPermissionToGenerateReport = useMemo(() => {
    const allowedRoles = [Role.Owner, Role.Admin, Role.Demo]
    const hasRole = hasAnyRole(allowedRoles, user)
    return hasRole
  }, [user])

  const generatePerformanceReport = async () => {
    if (noActiveCpis) {
      dispatch(
        openToast({
          variant: ToastVariant.warning,
          props: {
            text: 'Report Generation Error',
            description:
              'No active CPIs were found. Please activate CPIs to generate a report.'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      return
    }

    try {
      setIsGenerating(true)
      const tenant = await TenantServiceV3.getTenant()

      const request: CreateReportRequest = {
        name: 'Program Report',
        type: ReportType.PERFORMANCE_REPORT,
        config: {
          reportName: 'Program Report',
          framework: tenant.cyFramework,
          categories: [],
          frequency: 'daily',
          dateFrom: null,
          dateTo: null,
          timePeriod: '3m',
          isIncludeCPIAppendix: true,
          isIncludePublicLabels: true,
          type: 'general'
        }
      }

      await generateAndDownloadReport(request)
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <>
      <ReportCardV2
        reportIcon={<PerformanceReportIcon />}
        title="Program Report"
        description="Generate trends across all active CPIs with a default daily view over 3 months. Customize the timeframe, frequency, and CPIs to match your needs."
        hasCustomizedSettings={true}
        hasPermission={hasPermissionToGenerateReport}
        isLoading={isGenerating}
        onDownload={generatePerformanceReport}
        onTemplateSaved={onTemplateSaved}
      />
    </>
  )
}

export default PerformanceReport
